// src/pages/Auth/index.js
import React, { useEffect, useMemo } from "react";
import { Card, Form, Input, Button, Typography, message, Tabs } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const { Title } = Typography;

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, loading, isAuthed } = useAuth();

  const fromPath = useMemo(() => {
    const state = location.state;
    if (state?.from?.pathname) return state.from.pathname;
    return "/home";
  }, [location.state]);

  useEffect(() => {
    if (isAuthed) {
      navigate(fromPath, { replace: true });
    }
  }, [isAuthed, navigate, fromPath]);

  const usernameRules = [
    { required: true, message: "请输入账号" },
    { min: 3, max: 20, message: "账号长度 3-20 位" },
    { pattern: /^[a-zA-Z0-9_]+$/, message: "仅支持字母/数字/下划线" },
  ];

  const passwordRules = [
    { required: true, message: "请输入密码" },
    { min: 6, message: "密码至少 6 位" },
  ];

  const handleLogin = async (values) => {
    const username = values.username?.trim();
    const password = values.password?.trim();
    const res = await login({ username, password });
    if (!res?.ok) {
      message.error(res?.msg || "登录失败");
      return;
    }
    localStorage.setItem("show_intro_modal", "true");
    navigate(fromPath, { replace: true });
  };

  const handleRegister = async (values) => {
    const username = values.username?.trim();
    const password = values.password?.trim();
    const res = await register({ username, password });
    if (!res?.ok) {
      message.error(res?.msg || "注册失败");
      return;
    }
    message.success("注册成功");
    localStorage.setItem("show_intro_modal", "true");
    navigate(fromPath, { replace: true });
  };

  return (
    <div
      style={{
        minHeight: "calc(100vh - 64px)", // 减去 Header 高度
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: "var(--app-bg)",
      }}
    >
      <div
        style={{
          position: "absolute",
          width: 260,
          height: 260,
          borderRadius: "50%",
          left: "8%",
          top: "12%",
          background: "var(--glow-1)",
          opacity: 0.35,
          filter: "blur(8px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 320,
          height: 320,
          borderRadius: "50%",
          right: "10%",
          bottom: "8%",
          background: "var(--glow-2)",
          opacity: 0.32,
          filter: "blur(10px)",
        }}
      />

      <Card
        style={{
          width: 380,
          borderRadius: 16,
          border: "1px solid var(--card-border)",
          background: "var(--card-bg)",
          boxShadow: "var(--card-shadow)",
          backdropFilter: "var(--card-blur)",
        }}
        bodyStyle={{ padding: "28px 30px 26px" }}
      >
        <div
          style={{
            width: 42,
            height: 6,
            borderRadius: 999,
            margin: "0 auto 16px",
            background: "var(--accent-gradient)",
          }}
        />
        <Title
          level={3}
          style={{
            textAlign: "center",
            marginBottom: 6,
            fontWeight: 600,
            letterSpacing: 1,
            color: "var(--text-primary)",
          }}
        >
          欢迎回来
        </Title>
        <Typography.Text
          style={{
            display: "block",
            textAlign: "center",
            marginBottom: 24,
            color: "var(--text-secondary)",
          }}
        >
          用你的账号继续探索灵感
        </Typography.Text>

        <Tabs
          defaultActiveKey="login"
          items={[
            {
              key: "login",
              label: "登录",
              children: (
                <Form
                  layout="vertical"
                  onFinish={handleLogin}
                  requiredMark={false}
                >
                  <Form.Item label="账号" name="username" rules={usernameRules}>
                    <Input
                      size="large"
                      placeholder="请输入账号"
                      autoComplete="username"
                      style={{
                        borderRadius: 10,
                        background: "var(--input-bg)",
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="密码"
                    name="password"
                    rules={passwordRules}
                  >
                    <Input.Password
                      size="large"
                      placeholder="请输入密码"
                      autoComplete="current-password"
                      style={{
                        borderRadius: 10,
                        background: "var(--input-bg)",
                      }}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={loading}
                    style={{
                      marginTop: 8,
                      height: 44,
                      borderRadius: 12,
                      border: 0,
                      background: "var(--accent-gradient)",
                      boxShadow: "var(--accent-shadow)",
                    }}
                  >
                    登录
                  </Button>
                </Form>
              ),
            },
            {
              key: "register",
              label: "注册",
              children: (
                <Form
                  layout="vertical"
                  onFinish={handleRegister}
                  requiredMark={false}
                >
                  <Form.Item label="账号" name="username" rules={usernameRules}>
                    <Input
                      size="large"
                      placeholder="请输入账号"
                      autoComplete="username"
                      style={{
                        borderRadius: 10,
                        background: "var(--input-bg)",
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="密码"
                    name="password"
                    rules={passwordRules}
                  >
                    <Input.Password
                      size="large"
                      placeholder="至少 6 位"
                      autoComplete="new-password"
                      style={{
                        borderRadius: 10,
                        background: "var(--input-bg)",
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="确认密码"
                    name="confirmPassword"
                    dependencies={["password"]}
                    rules={[
                      { required: true, message: "请再次输入密码" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("两次密码不一致"));
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      size="large"
                      placeholder="再次输入密码"
                      autoComplete="new-password"
                      style={{
                        borderRadius: 10,
                        background: "var(--input-bg)",
                      }}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={loading}
                    style={{
                      marginTop: 8,
                      height: 44,
                      borderRadius: 12,
                      border: 0,
                      background: "var(--accent-gradient)",
                      boxShadow: "var(--accent-shadow)",
                    }}
                  >
                    注册
                  </Button>
                </Form>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
